import React, { useState, useEffect } from 'react';
import {
  AlertCircle, CheckCircle, Copy, Eye, EyeOff, Zap, Users,
  ShoppingBag, TrendingUp, Clock, ChevronLeft, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import Footer from '../shared/Footer';

const API_BASE_URL = getApiBaseUrl();

const DistributorInfo = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [isNotDistributor, setIsNotDistributor] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [customersPage, setCustomersPage] = useState(0);

  const LEDGER_LIMIT = 10;
  const CUSTOMERS_LIMIT = 10;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      // 获取分销商信息
      const infoRes = await fetch(`${API_BASE_URL}/commission/info`, {
        credentials: 'include'
      });
      const infoData = await infoRes.json();

      if (!infoRes.ok) {
        setError(infoData.detail || '无法加载分销商信息');
        return;
      }

      if (!infoData.is_distributor) {
        setIsNotDistributor(true);
        return;
      }

      setInfo(infoData);

      // 获取钱包信息
      const walletRes = await fetch(`${API_BASE_URL}/commission/wallet`, {
        credentials: 'include'
      });
      if (walletRes.ok) {
        setWallet(await walletRes.json());
      }

      // 获取流水信息
      const ledgerRes = await fetch(
        `${API_BASE_URL}/commission/ledger?skip=0&limit=${LEDGER_LIMIT}`,
        { credentials: 'include' }
      );
      if (ledgerRes.ok) {
        setLedger(await ledgerRes.json());
      }

      // 获取客户列表
      const customersRes = await fetch(
        `${API_BASE_URL}/commission/customers?skip=0&limit=${CUSTOMERS_LIMIT}`,
        { credentials: 'include' }
      );
      if (customersRes.ok) {
        setCustomers(await customersRes.json());
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (info?.invitation_code) {
      navigator.clipboard.writeText(info.invitation_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const loadLedgerPage = async (page) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/commission/ledger?skip=${page * LEDGER_LIMIT}&limit=${LEDGER_LIMIT}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setLedger(await res.json());
        setLedgerPage(page);
      }
    } catch (err) {
      console.error('加载流水失败', err);
    }
  };

  const loadCustomersPage = async (page) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/commission/customers?skip=${page * CUSTOMERS_LIMIT}&limit=${CUSTOMERS_LIMIT}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setCustomers(await res.json());
        setCustomersPage(page);
      }
    } catch (err) {
      console.error('加载客户列表失败', err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-green-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 font-medium">加载中...</p>
        </div>
      </div>
    );
  }

  if (isNotDistributor) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">还不是分销商</h2>
            <p className="text-gray-600 mb-6">
              您还没有申请成为分销商。申请成为分销商后，您将获得唯一的邀请码，通过分享邀请码邀请客户，每个客户完成订单后您都能获得佣金！
            </p>
            <button
              onClick={() => navigate('/commission/apply')}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors mb-3"
            >
              立即申请
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-center text-xl font-bold text-gray-900 mb-4">加载失败</h2>
            <p className="text-center text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadData}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              重新加载
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const invitationLink = `${window.location.origin}/auth/register?invitation_code=${info.invitation_code}`;
  const statusColors = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800',
    rejected: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    active: '✅ 已激活',
    pending: '⏳ 待审核',
    suspended: '❌ 已暂停',
    rejected: '❌ 已拒绝'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 页头 */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-green-600 hover:text-green-700 font-medium mb-4"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            返回
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">分销商中心</h1>
          <p className="text-gray-600">管理您的邀请码、客户和佣金</p>
        </div>

        {/* 邀请码卡片 */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm opacity-90 mb-2">您的专属邀请码</p>
              <h2 className="text-4xl font-bold tracking-wider">{info.invitation_code}</h2>
            </div>
            <button
              onClick={copyToClipboard}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                copied
                  ? 'bg-white text-green-600'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }`}
            >
              <Copy className="h-5 w-5" />
              {copied ? '已复制' : '复制'}
            </button>
          </div>
          <p className="text-sm opacity-90 mb-4">
            邀请链接：{invitationLink}
          </p>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[info.status] || 'bg-gray-100 text-gray-800'}`}>
              {statusLabels[info.status] || '未知状态'}
            </span>
            {info.status === 'pending' && (
              <span className="text-xs opacity-90">审核中，请耐心等待...</span>
            )}
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* 佣金余额 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-600 text-sm font-medium">佣金余额</p>
              <Zap className="h-5 w-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">
              ¥{(wallet?.balance / 100 || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              总收入：¥{(wallet?.total_earned / 100 || 0).toFixed(2)}
            </p>
          </div>

          {/* 累计客户 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-600 text-sm font-medium">累计客户</p>
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-600">
              {info.total_customers || 0}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              已激活的客户数量
            </p>
          </div>

          {/* 累计订单 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-600 text-sm font-medium">累计订单</p>
              <ShoppingBag className="h-5 w-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-purple-600">
              {info.total_orders || 0}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              客户完成的订单数
            </p>
          </div>

          {/* 累计佣金 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-600 text-sm font-medium">累计佣金</p>
              <TrendingUp className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-red-600">
              ¥{(info.total_commission / 100 || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              历史总佣金
            </p>
          </div>
        </div>

        {/* 标签页 */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* 标签栏 */}
          <div className="border-b border-gray-200 flex">
            {[
              { id: 'overview', label: '概览', icon: '📊' },
              { id: 'ledger', label: '佣金流水', icon: '📝' },
              { id: 'customers', label: '客户列表', icon: '👥' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600 bg-green-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* 标签内容 */}
          <div className="p-6">
            {/* 概览 */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">分销商信息</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {info.applied_at && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">申请时间</p>
                        <p className="text-lg font-medium text-gray-900">
                          {new Date(info.applied_at).toLocaleDateString('zh-CN')}
                        </p>
                      </div>
                    )}
                    {info.approved_at && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">审核时间</p>
                        <p className="text-lg font-medium text-gray-900">
                          {new Date(info.approved_at).toLocaleDateString('zh-CN')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 flex items-start">
                    <span className="mr-2">ℹ️</span>
                    <span>
                      您可以将邀请码分享给任何想要办理签证的人。当他们通过您的邀请码注册并完成签证订单后，您将获得相应的佣金。越南签证每单佣金为 ¥5 元。
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* 佣金流水 */}
            {activeTab === 'ledger' && (
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">佣金流水记录</h3>
                {ledger && ledger.items && ledger.items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            类型
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            金额
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            说明
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            时间
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {ledger.items.map(item => (
                          <tr key={item._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                item.delta > 0
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {item.delta > 0 ? '+ ' : '- '}¥{Math.abs(item.delta / 100).toFixed(2)}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              ¥{(item.delta / 100).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.reason}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {new Date(item.created_at).toLocaleDateString('zh-CN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* 分页 */}
                    {ledger.total > LEDGER_LIMIT && (
                      <div className="flex justify-center items-center gap-2 mt-6">
                        <button
                          onClick={() => loadLedgerPage(ledgerPage - 1)}
                          disabled={ledgerPage === 0}
                          className="px-3 py-2 rounded border border-gray-300 disabled:opacity-50"
                        >
                          上一页
                        </button>
                        <span className="text-sm text-gray-600">
                          第 {ledgerPage + 1} / {Math.ceil(ledger.total / LEDGER_LIMIT)} 页
                        </span>
                        <button
                          onClick={() => loadLedgerPage(ledgerPage + 1)}
                          disabled={(ledgerPage + 1) * LEDGER_LIMIT >= ledger.total}
                          className="px-3 py-2 rounded border border-gray-300 disabled:opacity-50"
                        >
                          下一页
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">暂无佣金流水记录</p>
                  </div>
                )}
              </div>
            )}

            {/* 客户列表 */}
            {activeTab === 'customers' && (
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">客户列表</h3>
                {customers && customers.items && customers.items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            用户名
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            注册时间
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            订单数
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            佣金
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {customers.items.map(customer => (
                          <tr key={customer.username} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {customer.username}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(customer.registered_at).toLocaleDateString('zh-CN')}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                                {customer.total_orders || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-medium text-green-600">
                              ¥{(customer.total_commission / 100 || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* 分页 */}
                    {customers.total > CUSTOMERS_LIMIT && (
                      <div className="flex justify-center items-center gap-2 mt-6">
                        <button
                          onClick={() => loadCustomersPage(customersPage - 1)}
                          disabled={customersPage === 0}
                          className="px-3 py-2 rounded border border-gray-300 disabled:opacity-50"
                        >
                          上一页
                        </button>
                        <span className="text-sm text-gray-600">
                          第 {customersPage + 1} / {Math.ceil(customers.total / CUSTOMERS_LIMIT)} 页
                        </span>
                        <button
                          onClick={() => loadCustomersPage(customersPage + 1)}
                          disabled={(customersPage + 1) * CUSTOMERS_LIMIT >= customers.total}
                          className="px-3 py-2 rounded border border-gray-300 disabled:opacity-50"
                        >
                          下一页
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">暂无客户记录</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DistributorInfo;
